import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Users, DollarSign, TrendingUp, Pencil, Check, X, Loader2, Zap, Tag } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function StatPill({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center gap-3" data-testid={`pkg-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: color + '18', color }}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-[#94A3B8] font-medium">{label}</div>
        <div className="text-lg font-bold text-[#0A0F1C]">{value}</div>
      </div>
    </div>
  );
}

const TOOL_OPTS = [['jumpstart', '7-Stage Jumpstart'], ['nonprofit', 'Nonprofit Accelerator'], ['website_vision', 'Website Vision Builder'],
  ['clarity_hub', 'Clarity Hub'], ['blueprint', 'My Blueprint'], ['live_training', 'Live Training']];

function PackageTools({ brandSlug, pkg, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(pkg.tools_included || []);
  const save = async (next) => {
    setSel(next);
    try { await axios.put(`${API_URL}/api/admin/coaching-packages/${brandSlug}/${pkg.package_id}`, { tools_included: next }, { withCredentials: true }); onSaved(); }
    catch { toast.error('Could not save tools'); }
  };
  return (
    <span className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`pkg-tools-${pkg.package_id}`}
        className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] hover:bg-[#DBEAFE]">Tools included: {sel.length}</button>
      {open && (
        <span className="absolute left-0 top-6 z-20 w-60 bg-white border border-[#E2E8F0] rounded-xl shadow-lg p-2 block" data-testid={`pkg-tools-menu-${pkg.package_id}`}>
          {TOOL_OPTS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 text-xs font-medium text-[#0A0F1C] cursor-pointer">
              <input type="checkbox" checked={sel.includes(k)} onChange={() => save(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k])} data-testid={`pkg-tool-${pkg.package_id}-${k}`} /> {l}
            </label>
          ))}
          <span className="block text-[10px] text-[#94A3B8] px-2 pt-1">Clients who buy this package get these tools automatically.</span>
        </span>
      )}
    </span>
  );
}

function PackageRow({ brandSlug, pkg, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(pkg.price);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPrice(pkg.price); }, [pkg.price]);

  const save = async () => {
    const val = parseFloat(price);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid price'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/coaching-packages/${brandSlug}/${pkg.package_id}`,
        { price: val }, { withCredentials: true });
      toast.success(`${pkg.name} updated to ${money(val)}`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-t border-[#F1F5F9]" data-testid={`pkg-row-${pkg.package_id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A0F1C] text-sm flex items-center gap-2">
          {pkg.name}
          <PackageTools brandSlug={brandSlug} pkg={pkg} onSaved={onSaved} />
          {!pkg.is_active && <span className="text-[10px] font-bold uppercase text-[#94A3B8] bg-[#F1F5F9] px-1.5 py-0.5 rounded">Inactive</span>}
        </div>
        <div className="text-xs text-[#94A3B8]">{pkg.duration_label}</div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <div className="text-center w-16" data-testid={`pkg-enrollments-${pkg.package_id}`}>
          <div className="font-bold text-[#0A0F1C]">{pkg.enrollments}</div>
          <div className="text-[10px] text-[#94A3B8] uppercase">Enrolls</div>
        </div>
        <div className="text-center w-20">
          <div className="font-bold text-emerald-600">{money(pkg.revenue)}</div>
          <div className="text-[10px] text-[#94A3B8] uppercase">Revenue</div>
        </div>

        {editing ? (
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8] text-sm">$</span>
              <input
                type="number" value={price} autoFocus
                onChange={(e) => setPrice(e.target.value)}
                className="w-28 pl-5 pr-2 py-1.5 border border-[#1D4ED8] rounded-lg text-sm focus:outline-none"
                data-testid={`pkg-price-input-${pkg.package_id}`}
              />
            </div>
            <button onClick={save} disabled={saving} className="p-1.5 bg-emerald-600 text-white rounded-lg disabled:opacity-60" data-testid={`pkg-save-${pkg.package_id}`}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            </button>
            <button onClick={() => { setEditing(false); setPrice(pkg.price); }} className="p-1.5 border border-[#E2E8F0] text-[#64748B] rounded-lg" data-testid={`pkg-cancel-${pkg.package_id}`}>
              <X size={15} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 font-bold text-[#0A0F1C] hover:text-[#1D4ED8] transition-colors w-28 justify-end"
            data-testid={`pkg-edit-${pkg.package_id}`}
          >
            {money(pkg.price)} <Pencil size={13} className="text-[#94A3B8]" />
          </button>
        )}
      </div>
    </div>
  );
}

function BrandCard({ brand, tiers, onChanged }) {
  const [applying, setApplying] = useState(null);

  const applyTier = async (tier) => {
    const preset = tiers[tier]?.by_order || [];
    if (!window.confirm(`Set ${brand.name} package prices to ${preset.map((p) => money(p)).join(' / ')}?`)) return;
    setApplying(tier);
    try {
      await axios.post(`${API_URL}/api/admin/coaching-packages/${brand.slug}/pricing-tier`,
        { tier }, { withCredentials: true });
      toast.success(`${tiers[tier].label} applied to ${brand.name}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply tier');
    } finally { setApplying(null); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-5" data-testid={`pkg-brand-${brand.slug}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {brand.logo_url
            ? <img src={brand.logo_url} alt={brand.name} className="h-7 object-contain" />
            : <Package size={18} style={{ color: brand.primary_color }} />}
          <h3 className="font-bold text-[#0A0F1C]">{brand.name}</h3>
          {brand.pricing_tier !== 'custom' && tiers[brand.pricing_tier] && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1D4ED8] bg-blue-50 px-2 py-0.5 rounded-full">
              <Tag size={11} /> {tiers[brand.pricing_tier].label}
            </span>
          )}
        </div>
      </div>

      {/* One-click pricing toggle */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3 mb-3" data-testid={`pkg-tier-toggle-${brand.slug}`}>
        <div className="flex items-center gap-1.5 text-xs font-bold text-[#475569] uppercase tracking-wide mb-2">
          <Zap size={13} className="text-amber-500" /> One-click pricing
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(tiers).map(([key, cfg]) => {
            const active = brand.pricing_tier === key;
            return (
              <button
                key={key}
                onClick={() => applyTier(key)}
                disabled={applying === key || active}
                className={`flex-1 min-w-[170px] text-left px-3 py-2 rounded-lg border transition-colors disabled:cursor-default ${active ? 'border-[#1D4ED8] bg-blue-50' : 'border-[#E2E8F0] bg-white hover:border-[#1D4ED8]'}`}
                data-testid={`pkg-tier-${brand.slug}-${key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-[#0A0F1C]">{cfg.label}</span>
                  {active ? <span className="text-[10px] font-bold text-[#1D4ED8] uppercase">Current</span>
                    : applying === key ? <Loader2 size={13} className="animate-spin text-[#1D4ED8]" /> : null}
                </div>
                <div className="text-xs text-[#1D4ED8] font-semibold mt-0.5">{cfg.by_order.map((p) => money(p)).join(' / ')}</div>
                <div className="text-[11px] text-[#94A3B8]">{cfg.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {brand.packages.map((p) => (
          <PackageRow key={p.package_id} brandSlug={brand.slug} pkg={p} onSaved={onChanged} />
        ))}
      </div>
    </div>
  );
}

export default function AdminCoachingPackages() {
  const [brands, setBrands] = useState([]);
  const [tiers, setTiers] = useState({});
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgRes, enrRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/coaching-packages`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/coaching-enrollments`, { withCredentials: true }),
      ]);
      setBrands(pkgRes.data.brands || []);
      setTiers(pkgRes.data.tiers || {});
      setEnrollments(enrRes.data.enrollments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load packages');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalEnroll = enrollments.length;
  const totalRevenue = enrollments.reduce((a, e) => a + Number(e.amount || 0), 0);
  const totalPackages = brands.reduce((a, b) => a + b.packages.length, 0);

  return (
    <AdminLayout title="Coaching Packages">
      <div data-testid="admin-coaching-packages-page" className="max-w-4xl">
        <div className="flex items-center gap-3 mb-1">
          <Package className="text-[#1D4ED8]" size={24} />
          <h1 className="heading-2 text-2xl text-[#0A0F1C]">Coaching Packages &amp; Enrollments</h1>
        </div>
        <p className="text-sm text-[#475569] mb-6 max-w-2xl">
          See who enrolled, track revenue per package, and adjust pricing instantly — no redeploy needed.
          Use the one-click toggle to shift to your Phase 2 prices once you have 10–15 success stories.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B] py-12" data-testid="pkg-loading">
            <Loader2 className="animate-spin" size={18} /> Loading packages…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatPill icon={Package} label="Packages" value={totalPackages} color="#1D4ED8" />
              <StatPill icon={Users} label="Enrollments" value={totalEnroll} color="#7C3AED" />
              <StatPill icon={DollarSign} label="Total Revenue" value={money(totalRevenue)} color="#059669" />
              <StatPill icon={TrendingUp} label="Avg / Enroll" value={money(totalEnroll ? totalRevenue / totalEnroll : 0)} color="#EA580C" />
            </div>

            {brands.length === 0 ? (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center text-[#64748B]" data-testid="pkg-empty">
                No brands have coaching packages yet. Add packages from Booking Brands.
              </div>
            ) : (
              brands.map((b) => (
                <BrandCard key={b.slug} brand={b} tiers={tiers} onChanged={load} />
              ))
            )}

            {/* Enrollments table */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mt-2" data-testid="pkg-enrollments-table">
              <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><Users size={16} /> Recent Enrollments</h3>
              {enrollments.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-4">No enrollments yet. They'll appear here the moment someone pays for a package.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#94A3B8] text-xs uppercase border-b border-[#F1F5F9]">
                        <th className="py-2 pr-4 font-semibold">Client</th>
                        <th className="py-2 pr-4 font-semibold">Package</th>
                        <th className="py-2 pr-4 font-semibold">Amount</th>
                        <th className="py-2 pr-4 font-semibold">Status</th>
                        <th className="py-2 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map((e) => (
                        <tr key={e.enrollment_id} className="border-b border-[#F8FAFC]" data-testid={`pkg-enrollment-${e.enrollment_id}`}>
                          <td className="py-2.5 pr-4">
                            <div className="font-semibold text-[#0A0F1C]">{e.client_name || '—'}</div>
                            <div className="text-xs text-[#94A3B8]">{e.client_email}</div>
                          </td>
                          <td className="py-2.5 pr-4 text-[#475569]">{e.package_name}</td>
                          <td className="py-2.5 pr-4 font-semibold text-emerald-600">{money(e.amount)}</td>
                          <td className="py-2.5 pr-4">
                            <span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{e.status || 'active'}</span>
                          </td>
                          <td className="py-2.5 text-xs text-[#64748B]">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
